import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors } from '../../styles/colors';
import { pickLocaleText } from '../../utils/publicPagesDefaults';
import { toRouterPath } from '../../utils/routerPath';
import { List } from './PublicLegalPageLayout';

const ResourceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ResourceItem = styled.li`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const resourceLinkStyles = `
  display: block;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  background: ${colors.sidebarBg};
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: ${colors.textPrimary};
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${colors.primaryDark};
    transform: translateY(-1px);
  }

  strong {
    color: ${colors.textPrimary};
  }

  span {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.75);
  }
`;

const ResourceAnchor = styled.a`
  ${resourceLinkStyles}
`;

const ResourceRouterLink = styled(Link)`
  ${resourceLinkStyles}
`;

export function PublicResourceLinksList({ resources, variant = 'cards' }) {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const active = (resources || []).filter((r) => r.enabled !== false && r.url);

  if (!active.length) return null;

  if (variant === 'inline') {
    return (
      <List>
        {active.map((res) => {
          const isInternal = res.url.startsWith('/');
          const text = pickLocaleText(res.descriptions, locale) || res.url;
          return (
            <li key={res.id}>
              <strong>
                {res.icon || '🔗'} {pickLocaleText(res.labels, locale)}
              </strong>
              {' — '}
              {isInternal ? (
                <ResourceRouterLink to={toRouterPath(res.url)} style={{ display: 'inline', padding: 0, border: 'none', background: 'transparent' }}>
                  {text}
                </ResourceRouterLink>
              ) : (
                <ResourceAnchor
                  href={res.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline', padding: 0, border: 'none', background: 'transparent' }}
                >
                  {text}
                </ResourceAnchor>
              )}
            </li>
          );
        })}
      </List>
    );
  }

  return (
    <ResourceList>
      {active.map((res) => {
        const label = (
          <>
            <strong>
              {res.icon} {pickLocaleText(res.labels, locale)}
            </strong>
            <span>{pickLocaleText(res.descriptions, locale)}</span>
          </>
        );
        const isInternal = res.url.startsWith('/');
        return (
          <ResourceItem key={res.id}>
            {isInternal ? (
              <ResourceRouterLink to={toRouterPath(res.url)}>{label}</ResourceRouterLink>
            ) : (
              <ResourceAnchor href={res.url} target="_blank" rel="noopener noreferrer">
                {label}
              </ResourceAnchor>
            )}
          </ResourceItem>
        );
      })}
    </ResourceList>
  );
}

export default PublicResourceLinksList;
